import { ExploreConfig, AnalysisMode, Audience, ExploreContext } from './types';

/**
 * Default analysis modes and their prompt templates
 */
const DEFAULT_MODE_PROMPTS: Record<string, string> = {
  general: `Please visit the interactive page above and analyze the visualization. Provide:
- Key insights about the patterns shown in the data
- Interpretation of what the data reveals
- Analysis of trends over time
- Implications for stakeholders
- Recommendations for further investigation`,

  trends: `Please visit the interactive page above and perform a trend analysis:
- Examine temporal patterns and changes over time
- Identify periods of significant change
- Analyze correlations between key variables
- Look for seasonal or cyclical patterns
- Highlight significant trend changes and their implications`,

  anomalies: `Please visit the interactive page above and identify anomalies:
- Detect unusual patterns or data points
- Identify periods where data behaved unexpectedly
- Flag potential data points that indicate significant events
- Analyze outliers in context
- Suggest explanations for anomalous readings`,

  policy: `Please visit the interactive page above and provide policy analysis:
- Assess policy implications based on the data
- Analyze potential impacts on different stakeholders
- Evaluate regulatory considerations
- Consider governance implications
- Recommend policy interventions based on the findings`
};

/**
 * Audience-specific instructions for prompt customization
 */
const AUDIENCE_INSTRUCTIONS: Record<Audience, string> = {
  general: "Explain clearly for a general audience using accessible language and avoiding technical jargon.",
  journalist: "Focus on narrative, key changes, and why it matters. Emphasize newsworthy angles and human impact.",
  researcher: "Emphasize methodology, data structure, limitations, and statistical significance. Include technical details.",
  policy: "Highlight implications, risks, and policy relevance. Focus on actionable insights for decision-makers."
};

/**
 * Builds structured prompts for AI analysis
 */
export class PromptBuilder {
  private config: ExploreConfig;

  constructor(config: ExploreConfig) {
    this.config = config;
  }

  /**
   * Generate a prompt for the specified analysis mode and audience
   */
  buildPrompt(mode: AnalysisMode = 'general', audience?: Audience, context?: ExploreContext): string {
    console.log('PromptBuilder.buildPrompt called with:', { mode, audience, context });
    
    const baseContext = this.buildBaseContext();
    const modePrompt = this.getModePrompt(mode);
    const audienceInstruction = this.buildAudienceInstruction(audience);
    const contextBlock = this.buildContextBlock(context);
    
    console.log('Audience instruction generated:', audienceInstruction);
    
    return `${baseContext}\n\n${modePrompt}\n\n${audienceInstruction}${contextBlock}`;
  }

  /**
   * Build the base context section of the prompt
   */
  private buildBaseContext(): string {
    const parts = [
      `Research Topic: ${this.config.title}`,
      ``,
      `Description: ${this.config.description}`,
      `Source: ${this.config.sourceName}`,
      `Interactive Page: ${this.config.sourceUrl}`
    ];

    // Add optional fields if present
    if (this.config.format) {
      parts.push(`Type: ${this.config.format}`);
    }

    if (this.config.lastUpdated) {
      parts.push(`Last Updated: ${this.config.lastUpdated}`);
    }

    if (this.config.fields && this.config.fields.length > 0) {
      parts.push(``, `Key Fields:`);
      this.config.fields.forEach(field => {
        const fieldDesc = field.description ? ` - ${field.description}` : '';
        parts.push(`- ${field.name}${fieldDesc}`);
      });
    }

    if (this.config.downloadUrl) {
      parts.push(``, `Data Download: ${this.config.downloadUrl}`);
    }

    return parts.join('\n');
  }

  /**
   * Get the prompt template for a specific analysis mode
   */
  private getModePrompt(mode: AnalysisMode): string {
    return DEFAULT_MODE_PROMPTS[mode] || DEFAULT_MODE_PROMPTS.general;
  }

  /**
   * Get available analysis modes for this configuration
   */
  getAvailableModes(): string[] {
    return this.config.modes || Object.keys(DEFAULT_MODE_PROMPTS);
  }

  /**
   * Validate that a mode is available for this configuration
   */
  isModeAvailable(mode: AnalysisMode): boolean {
    const availableModes = this.getAvailableModes();
    return availableModes.includes(mode);
  }

  /**
   * Build audience-specific instruction
   */
  private buildAudienceInstruction(audience?: Audience): string {
    if (!audience) {
      return '';
    }

    const instruction = AUDIENCE_INSTRUCTIONS[audience];
    return `\nAudience Context:\n${instruction}`;
  }

  /**
   * Build context block from real-time page context
   */
  private buildContextBlock(context?: ExploreContext): string {
    if (!context) {
      return '';
    }

    const contextParts: string[] = [];

    if (context.selectedSeries?.length) {
      contextParts.push(`Selected Series: ${context.selectedSeries.join(', ')}`);
    }

    if (context.dateRange) {
      contextParts.push(`Date Range: ${context.dateRange[0]} to ${context.dateRange[1]}`);
    }

    if (context.filters && Object.keys(context.filters).length > 0) {
      const filterStrings = Object.entries(context.filters)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
      contextParts.push(`Applied Filters: ${filterStrings}`);
    }

    if (context.chartTitle) {
      contextParts.push(`Current Chart: ${context.chartTitle}`);
    }

    if (context.tableState) {
      contextParts.push(`Table State: ${context.tableState}`);
    }

    if (context.articleContext) {
      contextParts.push(`Page Context: ${context.articleContext}`);
    }

    if (contextParts.length === 0) {
      return '';
    }

    return `\n\nAdditional Context:\n${contextParts.join('\n')}\n\nPrioritize this context in your analysis.`;
  }

  /**
   * Generate a prompt using the legacy method signature
   */
  generatePrompt(mode: AnalysisMode): string {
    return this.buildPrompt(mode);
  }

  /**
   * Get a preview of what the prompt will look like
   */
  previewPrompt(mode: AnalysisMode): {
    baseContext: string;
    modePrompt: string;
    fullPrompt: string;
  } {
    const baseContext = this.buildBaseContext();
    const modePrompt = this.getModePrompt(mode);
    const fullPrompt = this.generatePrompt(mode);

    return {
      baseContext,
      modePrompt,
      fullPrompt
    };
  }
}
